import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faCalendarAlt, faClock, faUsers, faQuestionCircle, faEye } from '@fortawesome/free-solid-svg-icons';
import productService from '../../../services/productService';
import ProductDetails from './ProductDetails';

// Estilos para o componente de produtos
const ProductsContainer = styled.div`
  padding: 2rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(100, 182, 255, 0.2);
  margin-bottom: 2rem;
  position: relative;
  overflow: hidden;
`;

const ProductsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ProductsTitle = styled.h2`
  color: #fff;
  font-size: 1.5rem;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: #64B6FF;
  }
`;

const AddProductButton = styled.button`
  background: linear-gradient(135deg, #4A00E0, #8E2DE2);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.6rem 1.2rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(142, 45, 226, 0.4);
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: all 0.6s ease;
  }
  
  &:hover:before {
    left: 100%;
  }
`;

const ProductsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
`;

const ProductCard = styled.div`
  background: rgba(20, 30, 48, 0.6);
  border-radius: 10px;
  padding: 1.5rem;
  border: 1px solid rgba(100, 182, 255, 0.1);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
    border-color: rgba(100, 182, 255, 0.3);
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #4A00E0, #8E2DE2);
  }
`;

const ProductName = styled.h3`
  color: #fff;
  font-size: 1.2rem;
  margin: 0 0 1rem 0;
`;

const ProductDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.8rem;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  
  svg {
    color: #64B6FF;
    width: 16px;
  }
`;

const ProductActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.8rem;
  margin-top: 1.5rem;
`;

const ActionButton = styled.button`
  background: transparent;
  color: ${props => props.delete ? '#ff6b6b' : '#64B6FF'};
  border: 1px solid ${props => props.delete ? 'rgba(255, 107, 107, 0.3)' : 'rgba(100, 182, 255, 0.3)'};
  border-radius: 6px;
  padding: 0.4rem 0.8rem;
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.delete ? 'rgba(255, 107, 107, 0.1)' : 'rgba(100, 182, 255, 0.1)'};
    border-color: ${props => props.delete ? 'rgba(255, 107, 107, 0.5)' : 'rgba(100, 182, 255, 0.5)'};
  }
`;

const TechGrid = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    linear-gradient(to right, rgba(100, 182, 255, 0.05) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(100, 182, 255, 0.05) 1px, transparent 1px);
  background-size: 20px 20px;
  z-index: -1;
`;

const DataStream = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: -1;
  
  &:before, &:after {
    content: '';
    position: absolute;
    width: 200px;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(100, 182, 255, 0.5), transparent);
    animation-duration: 6s;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
  }
  
  &:before {
    top: 20%;
    left: -10%;
    animation-name: dataStream1;
  }
  
  &:after {
    top: 70%;
    right: -10%;
    animation-name: dataStream2;
  }
  
  @keyframes dataStream1 {
    0% { transform: translateX(0) scaleX(1); opacity: 0; }
    10% { opacity: 1; }
    90% { opacity: 1; }
    100% { transform: translateX(calc(100vw + 200px)) scaleX(0.5); opacity: 0; }
  }
  
  @keyframes dataStream2 {
    0% { transform: translateX(0) scaleX(1); opacity: 0; }
    10% { opacity: 1; }
    90% { opacity: 1; }
    100% { transform: translateX(calc(-100vw - 200px)) scaleX(0.5); opacity: 0; }
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 24px;
  height: 24px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;
  margin-right: 0.5rem;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// Componente principal
const UserProducts = () => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [showProductDetails, setShowProductDetails] = useState(false);
  const navigate = useNavigate();
  
  // Carregar produtos do usuário ao montar o componente
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setIsLoading(true);
        const userProducts = productService.getUserProducts();
        setProducts(userProducts);
      } catch (error) {
        console.error('Erro ao carregar produtos:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadProducts();
  }, []);
  
  // Navegar para a página de adição de produto
  const handleAddProduct = () => {
    navigate('/novo-produto');
  };
  
  // Visualizar detalhes de um produto
  const handleViewProduct = (productId) => {
    setSelectedProductId(productId);
    setShowProductDetails(true);
  };
  
  // Editar um produto existente
  const handleEditProduct = (productId) => {
    // Implementação futura: navegação para a página de edição
    console.log('Editar produto:', productId);
    // Fechar o modal de detalhes se estiver aberto
    setShowProductDetails(false);
  };
  
  // Remover um produto
  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Tem certeza que deseja remover este produto?')) {
      try {
        await productService.deleteProduct(productId);
        // Atualizar a lista de produtos após a remoção
        setProducts(productService.getUserProducts());
      } catch (error) {
        console.error('Erro ao remover produto:', error);
      }
    }
  };
  
  // Fechar o modal de detalhes
  const handleCloseProductDetails = () => {
    setShowProductDetails(false);
    setSelectedProductId(null);
  };
  
  return null;
};

export default UserProducts;
